import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gameApi } from '../../services/api/game.api';
import { settingsApi } from '../../services/api/settings.api';
import { useStore } from '../../store';
import { getPlayerRealtimeSocket } from '../../services/realtime/player.socket';
import {
  playerRealtimeEvents,
  type PlayerTeamResetEvent,
  type TeamName,
  type RoundNumber,
} from '@business-game/shared';
import { sessionsApi } from '../../services/api/sessions.api';
import { applyPlayerLanguage } from '../../utils/sessionI18n';

export const End: React.FC = () => {
  const navigate = useNavigate();
  const { sessionCode: sessionCodeFromPath } = useParams<{ sessionCode?: string }>();
  const { t } = useTranslation();
  const chartRef = useRef<HTMLDivElement>(null);
  const apexChartsRef = useRef<any>(null);
  const resetGame = useStore((state) => state.resetGame);

  const [scores, setScores] = useState<{
    display_1: number;
    display_2: number;
    display_3: number;
    display_4: number | null;
  } | null>(null);
  const [lastVerbatim, setLastVerbatim] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<{
    business: number[];
    employees: number[];
    society: number[];
    environment: number[];
    labels: string[];
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const teamName = localStorage.getItem('team_name') as TeamName | null;
  const storedTeamId = localStorage.getItem('team_id');
  const teamId = storedTeamId ? parseInt(storedTeamId, 10) : null;
  const sessionCode = sessionCodeFromPath || localStorage.getItem('session_code');

  useEffect(() => {
    if (sessionCode && sessionCodeFromPath !== sessionCode) {
      navigate(`/end/${sessionCode}`, { replace: true });
    }
  }, [navigate, sessionCode, sessionCodeFromPath]);

  // No team in storage: nothing to show, go back to team selection
  // (previously teamId silently fell back to team 1 and showed its graph).
  useEffect(() => {
    if (!teamName || !teamId) {
      navigate(sessionCode ? `/select-player/${sessionCode}` : '/select-player', { replace: true });
    }
  }, [navigate, sessionCode, teamId, teamName]);

  useEffect(() => {
    const loadData = async () => {
      if (!teamName || !teamId) {
        setLoading(false);
        return;
      }
      try {
        const sessionCheck = sessionCode ? await sessionsApi.checkSession(sessionCode) : null;
        if (sessionCheck?.session) {
          await applyPlayerLanguage(sessionCheck.session.language, sessionCheck.session.gameMode);
        }

        const config = await settingsApi.getGameConfig();
        const rounds = config.numberOfRounds ?? 10;
        const lastRound = Math.min(rounds, 10) as RoundNumber;

        const [scoreData, graphDataRes] = await Promise.all([
          gameApi.calculateScore({
            teamName,
            currentRound: lastRound,
            sessionCode: sessionCode ?? undefined,
          }),
          gameApi.generateGraph({
            team: teamId,
            round: lastRound,
            session: sessionCode ?? undefined,
          }),
        ]);
        setScores(scoreData);
        setGraphData(graphDataRes);
      } catch (error) {
        console.error('Failed to load end data:', error);
      } finally {
        setLoading(false);
      }
    };

    const storedVerbatim = localStorage.getItem('last_verbatim');
    setLastVerbatim(storedVerbatim);

    loadData();
  }, [teamName, teamId, sessionCode]);

  useEffect(() => {
    if (!teamName || !sessionCode) {
      return;
    }

    const socket = getPlayerRealtimeSocket();

    const handleTeamReset = (event: PlayerTeamResetEvent) => {
      if (event.teamName !== teamName || event.sessionCode !== sessionCode) {
        return;
      }

      resetGame();
      navigate(`/select-player/${sessionCode}`, { replace: true });
    };

    const joinRoom = () => {
      socket.emit(playerRealtimeEvents.joinTeamSession, { sessionCode, teamName });
    };

    if (!socket.connected) {
      socket.connect();
    }

    joinRoom();
    // Re-join the room after every (re)connection: a reconnected socket has a
    // new id and lost all its room memberships on the server.
    socket.on('connect', joinRoom);
    socket.on(playerRealtimeEvents.teamReset, handleTeamReset);

    return () => {
      socket.off('connect', joinRoom);
      socket.emit(playerRealtimeEvents.leaveTeamSession, { sessionCode, teamName });
      socket.off(playerRealtimeEvents.teamReset, handleTeamReset);
    };
  }, [navigate, resetGame, sessionCode, teamName]);

  const isTeamlabGraph = graphData ? graphData.environment.length === 0 : true;
  const isTeamlabScores = scores?.display_4 == null;

  // Render ApexCharts when graph data is available
  useEffect(() => {
    if (!graphData || !chartRef.current) return;

    import('apexcharts').then((ApexChartsModule) => {
      const ApexCharts = ApexChartsModule.default;
      const chartElement = chartRef.current;

      if (!chartElement) {
        return;
      }

      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
      }

      const series = [
        { name: t('game.impact_performance'), data: graphData.business },
        { name: t('game.impact_cohesion'), data: graphData.employees },
        { name: t('game.impact_wellbeing'), data: graphData.society },
      ];
      if (graphData.environment.length > 0) {
        series.push({ name: t('game.impact_environment'), data: graphData.environment });
      }

      const translatedLabels = graphData.labels.map((label) => {
        if (label === 'Start' || label === 'Début' || label === 'Inicio') return t('game.graph_start');
        const periodMatch = label.match(/(?:Period|Période|Período)\s+(\d+)/i);
        return periodMatch ? t('game.graph_period', { number: periodMatch[1] }) : label;
      });

      const colors = isTeamlabGraph
        ? ['#3b82f6', '#ec4899', '#eab308']
        : ['#3b82f6', '#ec4899', '#eab308', '#10b981'];

      const chart = new ApexCharts(chartElement, {
        series,
        chart: {
          type: 'area',
          height: 320,
          fontFamily: 'Inter, sans-serif',
          toolbar: { show: false },
          background: 'transparent',
          animation: { enabled: true, easing: 'easeinout', speed: 800 },
        },
        colors,
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: 3, colors },
        markers: { size: 5, strokeWidth: 2, strokeColors: colors, fillColors: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'] },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.5, opacityTo: 0.1, stops: [0, 90, 100] } },
        xaxis: {
          categories: translatedLabels,
          labels: { style: { colors: '#64748b', fontSize: '11px', fontFamily: 'Inter, sans-serif' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#64748b', fontSize: '11px', fontFamily: 'Inter, sans-serif' } } },
        grid: { borderColor: '#f1f5f9', strokeDashArray: 4, padding: { top: 0, right: 0, bottom: 0, left: 10 } },
        tooltip: { theme: 'light', shared: true, intersect: false, y: { formatter: (val: number) => Math.round(val).toString() } },
        legend: { position: 'bottom', horizontalAlign: 'center', fontFamily: 'Inter, sans-serif', offsetY: 8, markers: { radius: 12 } },
      } as any);
      chart.render();
      apexChartsRef.current = chart;
    });

    return () => {
      if (apexChartsRef.current) {
        apexChartsRef.current.destroy();
        apexChartsRef.current = null;
      }
    };
  }, [graphData, isTeamlabGraph, t]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12">
      {/* Light blurred semi-transparent background */}
      <div className="fixed inset-0 bg-white/40 backdrop-blur-md -z-10" />

      <div className="max-w-4xl w-full rounded-3xl border border-slate-200/60 bg-white/70 backdrop-blur-xl p-10 shadow-xl shadow-slate-200/50">
        <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <p className="text-sm font-bold uppercase tracking-[0.3em] text-emerald-600 mb-3">
          {t('game.end_badge')}
        </p>
        <h1 className="text-2xl font-black tracking-tight text-slate-900 mb-4">
          {t('game.end_title')}
        </h1>
        <p className="text-slate-600 text-lg leading-relaxed mb-8">
          {t('game.end_subtitle')}
        </p>

        {/* Graph final */}
        {!loading && graphData && (
          <div className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
              {t('game.end_graph_title')}
            </h2>
            <div ref={chartRef} className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-4" />
          </div>
        )}

        {/* Scores */}
        {!loading && scores && (
          <div className="mb-8">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
              {t('game.end_scores_title')}
            </h2>
            <div className={`grid gap-3 ${isTeamlabScores ? 'grid-cols-3' : 'grid-cols-4'}`}>
              <div className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-4 text-center">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  {t('game.impact_performance', 'Performance')}
                </p>
                <p className="text-2xl font-black text-slate-900">{scores.display_1}</p>
              </div>
              <div className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-4 text-center">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  {t('game.impact_cohesion', 'Cohésion')}
                </p>
                <p className="text-2xl font-black text-slate-900">{scores.display_2}</p>
              </div>
              <div className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-4 text-center">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                  {t('game.impact_wellbeing', 'Bien-être au travail')}
                </p>
                <p className="text-2xl font-black text-slate-900">{scores.display_3}</p>
              </div>
              {!isTeamlabScores && scores.display_4 != null && (
                <div className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-4 text-center">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">
                    {t('game.impact_environment', 'Environnement')}
                  </p>
                  <p className="text-2xl font-black text-slate-900">{scores.display_4}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Verbatims finaux */}
        {lastVerbatim && (
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
              Feedback final
            </h2>
            <div
              className="rounded-xl bg-slate-50/80 border border-slate-200/60 p-5 prose prose-slate max-w-none prose-p:my-2 prose-p:text-slate-700 text-sm"
              dangerouslySetInnerHTML={{ __html: lastVerbatim }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
